import { supabase } from '../lib/supabase';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const reviewService = {
  /**
   * Fetch completed bookings that need reviews
   * @param {string} userId - Current user ID
   * @param {string} userRole - User role (owner or renter)
   * @returns {Promise<Array>} Array of bookings pending review
   */
  async getCompletedBookingsForReview(userId, userRole) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Fetch completed bookings
      let query = supabase
        ?.from('bookings')
        ?.select(`
          *,
          renter:user_profiles!bookings_renter_id_fkey(
            id,
            full_name,
            email,
            profile_photo_url
          ),
          owner:user_profiles!bookings_owner_id_fkey(
            id,
            full_name,
            email,
            profile_photo_url
          ),
          trailer:trailer_listings!bookings_trailer_listing_id_fkey(
            id,
            title,
            trailer_type,
            photos
          )
        `)
        ?.eq('status', 'completed');

      // Filter by user role
      if (userRole === 'owner') {
        query = query?.eq('owner_id', userId);
      } else {
        query = query?.eq('renter_id', userId);
      }

      const { data: bookings, error: bookingsError } = await query?.order('end_date', { ascending: false });

      if (bookingsError) {
        console.error('Error fetching completed bookings:', bookingsError);
        if (bookingsError?.code === 'PGRST116') {
          return [];
        }
        throw new Error(bookingsError?.message || 'Failed to fetch completed bookings');
      }

      if (!bookings || bookings?.length === 0) {
        return [];
      }

      // Check which bookings already have reviews
      const bookingIds = bookings?.map(b => b?.id);
      const { data: existingReviews, error: reviewsError } = await supabase
        ?.from('trailer_reviews')
        ?.select('booking_id')
        ?.in('booking_id', bookingIds)
        ?.eq('reviewer_id', userId);

      if (reviewsError) {
        console.error('Error fetching existing reviews:', reviewsError);
      }

      const reviewedBookingIds = new Set(existingReviews?.map(r => r?.booking_id) || []);

      // Filter out bookings that already have reviews
      const pendingReviews = bookings?.filter(b => !reviewedBookingIds?.has(b?.id));

      return pendingReviews?.map(toCamelCase) || [];
    } catch (error) {
      console.error('Error in getCompletedBookingsForReview:', error);
      throw error;
    }
  },

  /**
   * Submit a review for a completed booking
   * @param {Object} reviewData - Review data
   * @returns {Promise<Object>} Created review
   */
  async submitReview(reviewData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const snakeCaseData = toSnakeCase(reviewData);
      snakeCaseData.reviewer_id = user?.id;

      const { data, error } = await supabase
        ?.from('trailer_reviews')
        ?.insert(snakeCaseData)
        ?.select(`
          *,
          reviewer:user_profiles!trailer_reviews_reviewer_id_fkey(
            id,
            full_name,
            email,
            profile_photo_url
          ),
          reviewed_user:user_profiles!trailer_reviews_reviewed_user_id_fkey(
            id,
            full_name,
            email
          )
        `)
        ?.single();

      if (error) {
        console.error('Error submitting review:', error);
        throw new Error(error?.message || 'Failed to submit review');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in submitReview:', error);
      throw error;
    }
  },

  /**
   * Fetch reviews received by a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of received reviews
   */
  async getReceivedReviews(userId) {
    try {
      const { data, error } = await supabase
        ?.from('trailer_reviews')
        ?.select(`
          *,
          reviewer:user_profiles!trailer_reviews_reviewer_id_fkey(
            id,
            full_name,
            email,
            profile_photo_url,
            role
          ),
          booking:bookings!trailer_reviews_booking_id_fkey(
            id,
            booking_reference,
            start_date,
            end_date
          ),
          trailer:trailer_listings!trailer_reviews_trailer_listing_id_fkey(
            id,
            title,
            trailer_type
          )
        `)
        ?.eq('reviewed_user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching received reviews:', error);
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw new Error(error?.message || 'Failed to fetch received reviews');
      }

      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('Error in getReceivedReviews:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time review updates
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription channel
   */
  subscribeToReviews(userId, callback) {
    const channel = supabase
      ?.channel('reviews_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trailer_reviews',
          filter: `reviewed_user_id=eq.${userId}`
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Upload a review photo to storage
   * @param {File} file - Photo file
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Upload result with public URL
   */
  async uploadReviewPhoto(file, userId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${userId}/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;

      const { data, error } = await supabase?.storage
        ?.from('review-photos')
        ?.upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (error) {
        console.error('Upload review photo error:', error?.message);
        return { data: null, error };
      }

      const { data: urlData } = supabase?.storage
        ?.from('review-photos')
        ?.getPublicUrl(fileName);

      return { data: { path: fileName, publicUrl: urlData?.publicUrl }, error: null };
    } catch (error) {
      console.error('Upload review photo error:', error);
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Unsubscribe from reviews channel
   * @param {Object} channel - Subscription channel
   */
  unsubscribeFromReviews(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};